import React, { useState } from 'react';
import { Button } from '@asigna/kit';
import { makeStyles } from '@material-ui/core';
import { signatureVrsToRsv } from '@stacks/common';
import { LedgerError } from '@zondax/ledger-stacks';

import { CommonLedgerDeviceInlineWarnings } from '../../../hooks/ledger/ledger-inline-warnings';
import {
  connectLedgerStacksApp,
  getStacksAppVersion,
  signLedgerUtf8Message,
} from '../../../hooks/ledger/stacks-ledger-utils';
import { getLedgerStorage } from '../../../store/ledger/ledger-storage';
import { getStorageKeysForNetwork } from '../../../utils/LocalstorageUtils';
import { TransactionCommonDialog } from '../TransactionDialog/TransactionCommonDialog';

interface MessageSignDialogProps {
  open: boolean;
  message: string;
  onClose: () => void;
  onSuccess: (signature: string) => any;
}

const useStyles = makeStyles(() => ({
  wrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    marginTop: '32px',
    gap: '8px',
    marginBottom: '32px',
  },
  container: {
    display: 'grid',
    gap: 8,
  },
  inviteCode: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
  },
  error: {
    color: '#D92B2B',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '18px',
  },
  loader: {
    width: 50,
    height: 50,
    margin: '50px auto',
  },
}));

export const LedgerSignMessageDialog = (props: MessageSignDialogProps) => {
  const { open, onClose, onSuccess, message } = props;
  const [hasError] = useState(false);
  const [signMessageResponse, setSignMessageResponse] = useState<any>();
  const [awaitingSignature, setAwaitingSignature] = useState(false);

  const handleSignMessage = async () => {
    let stacksApp: Awaited<ReturnType<typeof connectLedgerStacksApp>> | null = null;
    try {
      stacksApp = await connectLedgerStacksApp();
      const ledgerStorage = getLedgerStorage();
      const ledgerInfo = ledgerStorage.getLedgerInfo();

      if (!ledgerInfo) {
        throw new Error('No ledger info found');
      }

      const versionInfo = await getStacksAppVersion(stacksApp);
      setSignMessageResponse(versionInfo);
      setAwaitingSignature(true);

      if (versionInfo.deviceLocked) {
        throw new Error('Device locked');
      }

      const derivation = localStorage.getItem(getStorageKeysForNetwork().derivationPath);

      if (!derivation) {
        throw new Error('No derivation saved');
      }

      const resp = await signLedgerUtf8Message(stacksApp)(message, derivation);

      if (resp.returnCode === LedgerError.DataIsInvalid) {
        throw new Error(LedgerError.DataIsInvalid.toString());
      }

      if (resp.returnCode === LedgerError.TransactionRejected) {
        throw new Error(LedgerError.TransactionRejected.toString());
      }

      if (resp.returnCode !== LedgerError.NoErrors) {
        throw new Error('Some other error');
      }

      const signature = signatureVrsToRsv(resp.signatureVRS.toString('hex'));

      onSuccess(signature);
    } catch (e) {
      console.error(e);
      setAwaitingSignature(false);
    } finally {
      if (stacksApp) {
        try {
          await stacksApp.transport.close();
        } catch {
          // ignore close errors
        }
      }
    }
  };

  const classes = useStyles();

  return (
    <TransactionCommonDialog
      open={open}
      title="Sign message"
      button={
        <Button
          fullWidth
          onClick={() => handleSignMessage()}
          disabled={awaitingSignature}
          message={awaitingSignature ? 'Confirm message' : 'Connect'}
          colorType="primary"
        />
      }
      onClose={onClose}
    >
      <div className={classes.wrapper}>
        {awaitingSignature && <>Please sign message on your ledger to proceed</>}
        {awaitingSignature ? (
          <img className={classes.loader} src="/loading-gif.gif" alt="" />
        ) : (
          <>
            <CommonLedgerDeviceInlineWarnings
              latestDeviceResponse={signMessageResponse}
              chain="Stacks"
              outdatedLedgerAppWarning={false}
            />
            {hasError && <span className={classes.error}>Error signing message</span>}
          </>
        )}
      </div>
    </TransactionCommonDialog>
  );
};
